import { Link } from "react-router-dom";
import content from "../../../config/content.json";

import { useCallback, useMemo } from "react";
import { NavItemProps } from "../../layout/Navigation/Navigation";
import NavGroup from "../../ui/NavGroup/NavGroup";
import NavGroupItems from "../../ui/NavGroupItems/NavGroupItems";
import CustomNavbar from "../../ui/Navbar/Navbar";
import Typography from "../../ui/Typography/Typography";
import "./Footer.scss";

type FooterColumnData = {
	title: string;
	links: NavItemProps[] | { [key: string]: NavItemProps[] };
};

type FooterDataProps = {
	[key: string]: FooterColumnData;
};

const getCurrentYear = new Date().getFullYear();
const copyrightText = `${getCurrentYear} Fly ORKA, Inc.`;

const FooterColumn = ({
	columnData,
	classKey,
}: {
	columnData: FooterColumnData;
	classKey: string;
}) => {
	const { title, links } = columnData;

	const renderCriteria: {
		DO_NOT_RENDER: boolean;
	} = useMemo(
		() => ({
			DO_NOT_RENDER: false,
		}),
		[],
	);

	const getUpdatedLinks = useCallback(
		(originalLinks: NavItemProps[]) => {
			if (!Array.isArray(originalLinks)) return originalLinks;
			return (originalLinks as NavItemProps[]).map((link) => {
				if (link?.requiredToRender) {
					return {
						...link,
						render: link?.requiredToRender
							? renderCriteria[
									link?.requiredToRender as keyof typeof renderCriteria
								]
							: true,
					};
				}
				return link;
			});
		},
		[renderCriteria],
	);

	const updatedLinks = getUpdatedLinks(links as NavItemProps[]);

	return (
		<li className={`footer__column footer__column--${classKey}`}>
			<Typography className="footer-column__nav-group__title">
				{title}
			</Typography>
			<ul className={"footer-column__nav-group-items nav__bar--column"}>
				{Array.isArray(updatedLinks) ? (
					<NavGroupItems items={updatedLinks} />
				) : (
					Object.entries(updatedLinks).map(([key, items]) => (
						<NavGroup
							className={`footer__nav-group footer__nav-group--${key} ${key === "social" ? "" : "nav-group--column"}`}
							key={key}>
							<NavGroupItems
								key={key}
								items={items as NavItemProps[]}
								isIconGroup={key === "social"}
								iconSize="2.5rem"
							/>
						</NavGroup>
					))
				)}
			</ul>
		</li>
	);
};

const FooterColumnsContainer = ({
	footerData,
}: {
	footerData: FooterDataProps;
}) => {
	return (
		<CustomNavbar className={"footer-navigation"}>
			<NavGroup className="footer__nav-group nav-group--column">
				{Object.entries(footerData).map(([key, columnData]) => (
					<FooterColumn key={key} classKey={key} columnData={columnData} />
				))}
			</NavGroup>
		</CustomNavbar>
	);
};

const Footer = () => {
	return (
		<footer className="site__footer">
			<Link to={content.header.home.url} className="home-link">
				<img
					src="/imagenes/footer-logo.svg"
					alt={content.header.home.label}
					className="logo"
				/>
			</Link>
			<FooterColumnsContainer footerData={content.footer as FooterDataProps} />
			<Typography className="copy-text text-light text-sm">
				&copy; {copyrightText}
			</Typography>
		</footer>
	);
};

export default Footer;
